/**
 * Type-safe route helpers, keyed to the authorization/route MANIFEST.
 *
 * Every call is checked against the manifest at compile time:
 *  - unknown key                       -> compiler error
 *  - a `page` key passed to forResource -> compiler error
 *  - a member arg on an index-only resource (`forResource('listing', ...)`)
 *                                       -> compiler error
 *  - a member the resource doesn't declare (`forResource('user', 'new')`
 *    when `user` has no `new` path)     -> compiler error
 *  - a hand-written path not in the manifest (`forPath('/typo')`)
 *                                       -> compiler error
 *
 * Rails/Nuxt-style ergonomics, but the manifest is the single source of truth.
 */
import { MANIFEST } from './manifest'

type Manifest = typeof MANIFEST
type Key = keyof Manifest

export type RouteId = string | number

/* -- key partitions by kind ------------------------------------------------ */
type ResourceKey = {
  [K in Key]: Manifest[K]['kind'] extends 'resource' ? K : never
}[Key]
type PageKey = {
  [K in Key]: Manifest[K]['kind'] extends 'page' ? K : never
}[Key]

/* -- per-resource path shape ----------------------------------------------- */
type PathsOf<K extends ResourceKey> = Manifest[K] extends { paths: infer P }
  ? P
  : never

/**
 * The set of member targets a given resource actually declares.
 * Resolves to `never` for index-only resources, which collapses the
 * member argument to "not allowed".
 */
type MemberArg<K extends ResourceKey> =
  | ('new' extends keyof PathsOf<K> ? 'new' : never)
  | ('show' extends keyof PathsOf<K> ? { show: RouteId } : never)
  | ('edit' extends keyof PathsOf<K> ? { edit: RouteId } : never)

/** `[]` when the resource is index-only; optional member otherwise. */
type MemberArgs<K extends ResourceKey> = [MemberArg<K>] extends [never]
  ? []
  : [member: MemberArg<K>] | []

/* -- concrete (non-templated) paths, for forPath --------------------------- */
type PagePath = { [K in PageKey]: Manifest[K]['path'] }[PageKey]
type IndexPath = {
  [K in ResourceKey]: PathsOf<K> extends { index: infer I }
    ? I extends string
      ? I
      : never
    : never
}[ResourceKey]
export type KnownPath = PagePath | IndexPath

/* -- impl ------------------------------------------------------------------ */
const fillTemplate = (template: string, id: RouteId): string =>
  template.replace(/:[A-Za-z][A-Za-z0-9]*/, String(id))

/**
 * Path for a resource. Index by default; `'new'` or `{ show }` / `{ edit }`
 * for member routes the resource declares.
 */
export function forResource<K extends ResourceKey>(
  key: K,
  ...member: MemberArgs<K>
): string {
  const paths = (MANIFEST[key] as { paths: Record<string, string> }).paths
  const arg = member[0] as MemberArg<K> | undefined

  if (arg === undefined) return paths.index
  if (arg === 'new') return paths.new
  if ('show' in arg) return fillTemplate(paths.show, arg.show)
  return fillTemplate(paths.edit, arg.edit)
}

/** Path for a standalone page. */
export function forPage<K extends PageKey>(key: K): string {
  return (MANIFEST[key] as { path: string }).path
}

/**
 * Escape hatch for a literal path. Still validated: only paths that exist in
 * the manifest (page paths + resource index paths) type-check.
 */
export function forPath(path: KnownPath): KnownPath {
  return path
}
